import type {ReactNode} from "react";

import {Button, Typography} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";

import {componentMessages} from "../../components.messages";

import {AppAlertDialog} from "./app-alert-dialog";

interface ConfirmDeleteDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title?: string;
  description?: ReactNode;
  itemName?: string;
  confirmLabel?: string;
}

export function ConfirmDeleteDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  isLoading = false,
  title,
  description,
  itemName,
  confirmLabel,
}: ConfirmDeleteDialogProps) {
  const {t} = useI18n();
  const resolvedTitle = title ?? t(componentMessages.confirmDeleteTitle);
  const resolvedConfirmLabel = confirmLabel ?? t(componentMessages.confirmDeleteConfirm);

  return (
    <AppAlertDialog.Root isOpen={isOpen} onOpenChange={onOpenChange}>
      <AppAlertDialog.Backdrop>
        <AppAlertDialog.Container>
          <AppAlertDialog.Dialog className="sm:max-w-100">
            <AppAlertDialog.CloseTrigger />
            <AppAlertDialog.Header>
              <AppAlertDialog.Icon status="danger" />
              <AppAlertDialog.Heading>{resolvedTitle}</AppAlertDialog.Heading>
            </AppAlertDialog.Header>
            <AppAlertDialog.Body>
              {description ? (
                typeof description === "string" ? (
                  <Typography>{description}</Typography>
                ) : (
                  description
                )
              ) : itemName ? (
                <Typography>
                  {t(componentMessages.confirmDeleteDescriptionWithItemPrefix)}{" "}
                  <strong>{itemName}</strong>.{" "}
                  {t(componentMessages.confirmDeleteDescriptionWithItemSuffix)}
                </Typography>
              ) : (
                <Typography>{t(componentMessages.confirmDeleteDescriptionGeneric)}</Typography>
              )}
            </AppAlertDialog.Body>
            <AppAlertDialog.Footer>
              <Button slot="close" variant="tertiary">
                {t(componentMessages.confirmDeleteCancel)}
              </Button>
              <Button
                isDisabled={isLoading}
                onPress={() => {
                  onConfirm();
                }}
                variant="danger"
              >
                {resolvedConfirmLabel}
              </Button>
            </AppAlertDialog.Footer>
          </AppAlertDialog.Dialog>
        </AppAlertDialog.Container>
      </AppAlertDialog.Backdrop>
    </AppAlertDialog.Root>
  );
}
